import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import App from "./App";
import { AuthProvider } from "./hooks/useAuth";
import { FlowProvider } from "./hooks/useFlow";
import DimoCallbackScreen from "./components/DimoCallbackScreen";
import DocumentCheckinForm from "./components/document-checkin/DocumentCheckinForm";
import ProtectedRoute from "./ProtectedRoute";
import { detectFlowType, FLOW_TYPES } from "./config/flowConfigs";
import MainLayout from "./components/MainLayout";
import Reward from "./components/rewards/Reward";
import RewardsClaim from "./components/rewards/RewardsClaim";
import ContactInfoScreen from "./components/ContactInfoScreen";

const AppWrapper = () => {
  // Detect the current flow type
  const flowType = detectFlowType();

  // Define protected steps for each flow
  const protectedSteps = {
    [FLOW_TYPES.INSURANCE_PROFILE]: [
      "dimo",
      "survey",
      "contact-info",
      "post-auth-survey",
      "insurance-options",
      "document",
      "canopy",
      "review-submit",
      "success",
    ],
    [FLOW_TYPES.EXTENDED_WARRANTY]: [
      "survey",
      "vehicle-check",
      "vehicle-selection",
      "vehicle-completion",
      "handoff-test",
      "document",
      "photo-capture",
      "handoff",
    ],
    [FLOW_TYPES.INSURANCE_RENEWAL]: [
      "contact-info",
      "survey",
      "insurance-options",
      "success",
    ],
    [FLOW_TYPES.REFINANCING]: [
      "contact-info",
      "survey",
      "insurance-options",
      "refinancing-policy-upload",
      "refinancing-vehicle-selection",
      "refinancing-vehicle-completion",
      "success",
    ],
  };

  // Get default routes based on flow type
  const getDefaultRoute = () => {
    if (flowType === FLOW_TYPES.EXTENDED_WARRANTY) {
      return "/extended-warranty/welcome";
    } else if (flowType === FLOW_TYPES.INSURANCE_RENEWAL) {
      return "/renewal/welcome";
    } else if (flowType === FLOW_TYPES.REFINANCING) {
      return "/refinance/welcome";
    } else {
      return "/onboarding/welcome";
    }
  };
  return (
    <AuthProvider>
      <FlowProvider flowType={flowType}>
        <BrowserRouter>
          <Routes>
            {/* Root redirect */}
            <Route
              path="/"
              element={<Navigate to={getDefaultRoute()} replace />}
            />

            {/* Legacy verification route */}
            <Route path="/verification" element={<App />} />

            {/* DIMO callback route */}
            <Route
              path="/onboarding/dimo-callback"
              element={<DimoCallbackScreen />}
            />

            {/* Insurance Profile Routes */}
            <Route
              path="/onboarding/:step"
              element={
                <RouteHandler
                  protectedSteps={protectedSteps[FLOW_TYPES.INSURANCE_PROFILE]}
                  flowType={FLOW_TYPES.INSURANCE_PROFILE}
                >
                  <App />
                </RouteHandler>
              }
            />

            {/* Extended Warranty Routes */}
            <Route
              path="/extended-warranty/:step"
              element={
                <RouteHandler
                  protectedSteps={protectedSteps[FLOW_TYPES.EXTENDED_WARRANTY]}
                  flowType={FLOW_TYPES.EXTENDED_WARRANTY}
                >
                  <App />
                </RouteHandler>
              }
            />

            {/* Renewal Routes */}
            <Route
              path="/renewal/:step"
              element={
                <RouteHandler
                  protectedSteps={protectedSteps[FLOW_TYPES.INSURANCE_RENEWAL]}
                  flowType={FLOW_TYPES.INSURANCE_RENEWAL}
                >
                  <App />
                </RouteHandler>
              }
            />

            {/* Refinance Routes */}
            <Route
              path="/refinance/:step"
              element={
                <RouteHandler
                  protectedSteps={protectedSteps[FLOW_TYPES.REFINANCING]}
                  flowType={FLOW_TYPES.REFINANCING}
                >
                  <App />
                </RouteHandler>
              }
            />

            {/* Rewards (protected by auth) */}
            <Route
              path="/rewards"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <div className="w-full max-w-3xl mx-auto">
                      <Reward />
                    </div>
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/rewards-claim"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <div className="w-full max-w-3xl mx-auto">
                      <RewardsClaim />
                    </div>
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/contact-info"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <div className="w-full max-w-3xl mx-auto">
                      <ContactInfoScreen />
                    </div>
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            <Route path="/checkin/:slug" element={<DocumentCheckinForm />} />

            {/* Fallback route */}
            <Route
              path="*"
              element={<Navigate to={getDefaultRoute()} replace />}
            />
          </Routes>
        </BrowserRouter>
      </FlowProvider>
    </AuthProvider>
  );
};

// Helper component to conditionally apply protection to routes
function RouteHandler({ children, protectedSteps, flowType }) {
  // Get step from current URL
  const pathname = window.location.pathname;
  const step = pathname.split("/").pop();
  console.log(step);
  // If step is in protectedSteps list, apply ProtectedRoute
  if (protectedSteps && protectedSteps.includes(step)) {
    console.log("yes this is a protected step= " + step);
    return <ProtectedRoute>{children}</ProtectedRoute>;
  }
  // Otherwise, render without protection
  return children;
}

export default AppWrapper;
